<template>
<div class="appeals-vue">

    <div v-if="local_appeals.length > 0">
        <h3>Appeals</h3>
        <p>Appeals allow you to define and manage your direct mail campaigns. You can specify appeal details, segments, and export settings.</p>
        <div class="card table-card my-3">
            <div class="card-header">
                <div class="row">
                    <div class="col-lg-6 col-12">
                        <h3 class="mt-2">Appeals ({{filtered_appeals.length}})</h3>
                    </div>
                    <div class="col-lg-6 d-none d-lg-block">
                        <div class="d-flex justify-content-end justify-self-end gap-2">
                            <div class="position-relative">
                                <input 
                                    type="text" 
                                    class="search-box form-control me-1 py-2" 
                                    placeholder="Search by Appeal Name..." 
                                    aria-label="Search by Appeal Name..." 
                                    :model-value="search_key"
                                    @update:model-value="search_key = $event; handleSearch"
                                >
                                <i class="fa fa-search search-placeholder-icon" v-if="search_key === ''"></i>
                            </div>

                            <a class="btn btn-primary" :href="'/direct_mail/'+ client.url +'/appeals/new'" v-if="canEdit('direct_mail')">
                                <img class="icon" src="/img/icons/avid-icons/plus.svg" alt="">Add New Appeal
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            <div class="card-body p-0">
                <table class="table table-with-pagination mb-0">
                    <thead>
                        <tr class="table-header-row">
                            <th @click="sortByKey('name')" class="table-column-1 d-table-cell">
                                <div class="d-flex flex-row">
                                    Name
                                    <div class="d-flex flex-column">
                                        <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{'sort-key-active': sort_key === 'name' && sort_order === 'asc'}" alt="">
                                        <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{'sort-key-active': sort_key === 'name' && sort_order === 'des'}" alt="">
                                    </div>
                                </div>
                            </th>
                            <th @click="sortByKey('drop_date')" class="table-column-3 d-table-cell">
                                <div class="d-flex flex-row">
                                    Drop Date
                                    <div class="d-flex flex-column">
                                        <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{'sort-key-active': sort_key === 'drop_date' && sort_order === 'asc'}" alt="">
                                        <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{'sort-key-active': sort_key === 'drop_date' && sort_order === 'des'}" alt="">
                                    </div>
                                </div>
                            </th>
                            <th @click="sortByKey('created_at')" class="table-column-3d-table-cell">
                                <div class="d-flex flex-row">
                                    Created
                                    <div class="d-flex flex-column">
                                        <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{'sort-key-active': sort_key === 'created_at' && sort_order === 'asc'}" alt="">
                                        <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{'sort-key-active': sort_key === 'created_at' && sort_order === 'des'}" alt="">
                                    </div>
                                </div>
                            </th>
                            <th class="column-sort-disabled d-table-cell text-nowrap pe-3" width="1" v-if="canEdit('direct_mail')">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(appeal, index) in list_shown" :key="appeal.id" :class="`table-row-${index+1}`">
                            <td class="d-table-cell align-middle"><a :href="'/direct_mail/'+ client.url +'/appeals/edit/' + appeal.id" >{{ appeal.name }}</a></td>
                            <td class="d-table-cell align-middle">
                                <span :title="datetime(appeal.drop_date)" data-bs-toggle="tooltip">
                                    {{ date(appeal.drop_date) }}
                                </span>
                            </td>
                            <td class="d-table-cell align-middle">{{ date(appeal.created_at) }}</td>
                            <td class="d-table-cell align-middle" v-if="canEdit('direct_mail')">
                                <div class="dropdown d-flex justify-content-center">
                                    <button class="btn btn-white btn-icon-only-md" data-bs-toggle="dropdown" data-bs-auto-close="true" aria-expanded="false" aria-label="Button">
                                        <img class="icon" src="/img/icons/avid-icons/dots-vertical.svg" data-bs-toggle="tooltip" title="Actions" alt="">
                                    </button>
                                    <div class="dropdown-menu dropdown-menu-primary dropdown-menu-end">
                                        <a class="dropdown-item" :href="'/direct_mail/'+ client.url +'/appeals/edit/' + appeal.id">
                                            <img class="icon me-2" src="/img/icons/avid-icons/edit.svg" alt=""> Edit Appeal
                                        </a>
                                        <a class="dropdown-item" :href="'/direct_mail/'+ client.url +'/appeals/clone/' + appeal.id">
                                            <img class="icon me-2" src="/img/icons/avid-icons/document-copy.svg" alt=""> Copy Appeal
                                        </a>
                                        <a class="dropdown-item" href="#" v-if="canExport('direct_mail') && appeal.exported_url && appeal.exported_url !== ''"
                                            @click.prevent="getFileUrl(appeal)">
                                            <span v-if="!isDownloading[appeal.id]">
                                                <img class="icon me-2" src="/img/icons/avid-icons/file-download.svg" alt=""> Download
                                            </span>
                                            <span v-else>
                                                <i class="fas fa-spinner fa-spin me-2"></i> Downloading...
                                            </span>
                                        </a>
                                        <hr class="my-1">
                                        <a class="dropdown-item text-danger" href="#" @click.prevent="archiveAppeal(appeal.id)">
                                            <img class="icon red-icon me-2" src="/img/icons/avid-icons/trash.svg" alt=""> Delete Appeal
                                        </a>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                    <tfoot class="table-border-bottom-0">
                        <tr>
                            <td colspan="100%" class="py-0">
                                <div class="table-pagination">
                                    <div class="table-pagination--group d-none d-md-flex">
                                        <div class="position-relative">
                                            <select 
                                                class="form-select" 
                                                name="num_per_page" 
                                                id="num_per_page" 
                                                v-model="num_per_page" 
                                                @change="reloadPagination(filtered_appeals);"
                                             aria-label="Num Per Page">
                                                <option value="5000">ALL</option>
                                                <option v-for="num in num_per_page_options" :key="num" :value="num">{{ num }}</option>
                                            </select>
                                            <div class="select-display-text">
                                                Showing {{ num_per_page === '5000' ? 'ALL' : num_per_page }}
                                            </div>
                                        </div>
                                    </div>

                                    <div class="table-pagination--group d-none d-lg-flex">
                                        {{ filtered_appeals.length === 0 ? '0' : item_interval }} of {{ filtered_appeals.length }}
                                    </div>

                                    <div class="table-pagination--group">
                                        <button v-if="page_count > 1" class="btn btn-secondary btn-icon-only-md me-1" @click="moveToPrevPage(filtered_appeals)" :disabled="page_index === 0" data-bs-toggle="tooltip" title="Previous page">
                                            <img class="icon" src="/img/icons/avid-icons/chevron-left.svg" alt="">
                                        </button>

                                        <div class="d-flex page-selector" v-if="page_index_array.length <= 3">
                                            <button v-for="page in page_index_array" :key="page" class="btn btn-none btn-icon-only-md" :class="page === (page_index + 1) ? 'current-page' : ''" @click="moveToPage(page, filtered_appeals)">
                                                {{ page }}
                                            </button>
                                        </div>
                                        <div class="d-flex page-selector" v-else>
                                            <button v-for="page in page_index_array.slice(0,2)" :key="page" class="btn btn-none btn-icon-only-md" :class="page === (page_index + 1) ? 'current-page' : ''" @click="moveToPage(page, filtered_appeals)">
                                                {{ page }}
                                            </button>

                                            <button v-if="page_index > 2" class="btn btn-none btn-icon-only-md text-nowrap" disabled>
                                            ...
                                            </button>
                                            <button v-if="page_index >= 2 && page_index < page_index_array.length -1" class="btn btn-none btn-icon-only-md current-page">
                                                {{ page_index + 1 }}
                                            </button>

                                            <button v-if="page_index < page_index_array.length -2" class="btn btn-none btn-icon-only-md text-nowrap" disabled aria-hidden="true">
                                            ...
                                            </button>
                                            <button :class="page_index === (page_index_array.length -1) ? 'current-page' : ''" class="btn btn-none btn-icon-only-md" @click="moveToPage(page_index_array.length, filtered_appeals)" :key="page_index_array.length">
                                                {{ page_index_array.length }}
                                            </button>
                                        </div>

                                        <button v-if="page_count > 1" class="btn btn-secondary btn-icon-only-md ms-1" @click="moveToNextPage(filtered_appeals)" :disabled="page_index === page_count -1" data-bs-toggle="tooltip" title="Next page">
                                            <img class="icon" src="/img/icons/avid-icons/chevron-right.svg" alt="">
                                        </button>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    </div>
    <div v-else>
        <h4>No appeals have been created yet.</h4>
        <p>
            Appeals allow you to define and manage your direct mail campaigns. You can specify appeal details, segments, and export settings.
        </p>
        <a :href="'/direct_mail/'+ client.url +'/appeals/new'" class="btn btn-primary mb-3" v-if="canEdit('direct_mail')">
            <span><i class="fas fa-fw fa-plus"></i>Add New Appeal</span>
        </a>
    </div>
</div>
</template>

<script>
import tablePaginationMixin from '../../mixins/table_pagination';
import permissionsMixin from '../../mixins/permissions'

export default {
    props: {
        client: {
            type: Object,
            required: true
        },
        appeals: {
            type: Array,
            required: true
        }
    },
    mixins: [tablePaginationMixin, permissionsMixin],
    data() {
        return {
            local_appeals: [],
            filtered_appeals: [],
            list_shown: [],
            search_key: '',
            sort_key: 'name',
            sort_order: 'asc',
            actions: {
                loading: false
            },
            isDownloading: {}
        };
    },
    beforeMount() {
        this.filtered_appeals = [...this.appeals];
        this.local_appeals = [...this.appeals];
        this.reloadPagination(this.filtered_appeals);
    },
    methods: {
        archiveAppeal(appeal_id) {
            const appeal = this.local_appeals.find(appeal => appeal.id == appeal_id);
            const index = this.local_appeals.findIndex(appeal => appeal.id == appeal_id);

            Swal.fire({
                title: 'Delete Appeal',
                text: `Are you sure you want to delete ${appeal.name}?`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Yes, delete",
                cancelButtonText: 'Cancel',
                customClass: {
                    popup: 'swal-warning'
                },
            }).then((result) => {
                if (result.isConfirmed) {
                    axios.delete(`/api/direct_mail/${appeal.id}`)
                        .then(() => {
                            this.local_appeals.splice(index, 1);
                            this.filtered_appeals = [...this.local_appeals];
                            this.reloadPagination(this.filtered_appeals);
                        });
                }
            });
        },

        getFileUrl(appeal) {
            this.isDownloading[appeal.id] = true;
            axios.post('/api/direct_mail/get_url', {
                url: appeal.exported_url,
                client_id: this.client.id
            }).then((res) => {
                var link = document.createElement("a");
                link.href = res.data.url;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }).finally(() => {
                this.isDownloading[appeal.id] = false;
            });
        },
        handleSearch() {
            this.filtered_appeals = this.appeals.filter(appeal =>
                appeal.name.toLowerCase().includes(this.search_key.toLowerCase())
            );
            this.page_index = 0;
            this.reloadPagination(this.filtered_appeals);
        },
        sortByKey(key) {
            if (this.sort_key === key) {
                this.sort_order = this.sort_order === 'asc' ? 'des' : 'asc';
            } else {
                this.sort_key = key;
                this.sort_order = 'asc';
            }

            this.filtered_appeals.sort((a, b) => {
                if (this.sort_order === 'asc') {
                    return (a[key] > b[key]) ? 1 : -1;
                } else {
                    return (a[key] < b[key]) ? 1 : -1;
                }
            });

            this.reloadPagination(this.filtered_appeals);
        },
    }
};
</script>